import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Collapse,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { DeleteOutline as DeleteOutlineIcon } from '@material-ui/icons';
import {
    TIER_DISCOUNT_TYPES,
    formatTierQuantityRange,
    getTierDiscountValueError,
    getTierMinQuantityError,
    sortTiersByMinQuantity,
} from '../../../constants/pricingTierConfig';

const tierCardStyle = (compact) => ({
    padding: compact ? 12 : 16,
    marginBottom: compact ? 10 : 12,
    borderRadius: 12,
    border: '1px solid #E8E8E8',
    background: '#fff',
});

const PricingTiersSection = ({
    tiers,
    discountType,
    storeCurrency,
    onDiscountTypeChange,
    onTierChange,
    onRemoveTier,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showHelp, setShowHelp] = useState(false);

    const sortedTiers = useMemo(() => sortTiersByMinQuantity(tiers), [tiers]);

    const valueAdornment =
        discountType === 'percentage' ? (
            <InputAdornment position="start">%</InputAdornment>
        ) : (
            <InputAdornment position="start">{storeCurrency || '$'}</InputAdornment>
        );

    const discountTypeLabel = isMobile ? 'Tipo de descuento' : 'Tipo de descuento (todos los niveles)';

    return (
        <Box>
            <FormControl variant="outlined" fullWidth style={{ marginBottom: isMobile ? 12 : 16 }}>
                <InputLabel id="tier-group-discount-type">{discountTypeLabel}</InputLabel>
                <Select
                    labelId="tier-group-discount-type"
                    label={discountTypeLabel}
                    value={discountType}
                    onChange={(e) => onDiscountTypeChange(e.target.value)}
                >
                    {TIER_DISCOUNT_TYPES.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {isMobile ? (
                <Box style={{ marginBottom: 12 }}>
                    <Button
                        size="small"
                        color="primary"
                        onClick={() => setShowHelp((v) => !v)}
                        style={{ textTransform: 'none', padding: 0, minWidth: 0, fontWeight: 600 }}
                    >
                        {showHelp ? 'Ocultar ayuda' : '¿Cómo funcionan los niveles?'}
                    </Button>
                    <Collapse in={showHelp}>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: 8, lineHeight: 1.45 }}
                        >
                            Cada nivel define desde qué cantidad aplica el descuento. El rango se
                            muestra debajo de cada nivel; el último aplica en adelante. Cada
                            descuento debe ser mayor que el del nivel anterior.
                        </Typography>
                    </Collapse>
                </Box>
            ) : (
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                    Define desde qué cantidad aplica cada descuento. El rango se muestra debajo de
                    cada nivel (el último aplica en adelante). Cada descuento debe ser mayor que el
                    del nivel anterior.
                </Typography>
            )}

            {tiers.map((tier, index) => {
                const sortedIndex = sortedTiers.findIndex((t) => t.localId === tier.localId);
                const rangeLabel =
                    sortedIndex >= 0 ? formatTierQuantityRange(sortedTiers, sortedIndex) : '';
                const minQtyError =
                    sortedIndex >= 0 ? getTierMinQuantityError(sortedTiers, sortedIndex) : null;
                const discountError =
                    sortedIndex >= 0
                        ? getTierDiscountValueError(sortedTiers, sortedIndex, discountType)
                        : null;

                return (
                <Paper key={tier.localId} elevation={0} style={tierCardStyle(isMobile)}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        marginBottom={1.5}
                    >
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            Nivel {index + 1}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => onRemoveTier(tier.localId)}
                            disabled={tiers.length <= 1}
                            aria-label={`Eliminar nivel ${index + 1}`}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Grid container spacing={isMobile ? 1.5 : 2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size={isMobile ? 'small' : 'medium'}
                                label="Cant. mínima"
                                type="number"
                                value={tier.min_quantity}
                                onChange={(e) =>
                                    onTierChange(tier.localId, { min_quantity: e.target.value })
                                }
                                inputProps={{ min: 1, step: 1 }}
                                error={Boolean(minQtyError)}
                                helperText={
                                    minQtyError ||
                                    (rangeLabel ? `Aplica: ${rangeLabel}` : undefined)
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size={isMobile ? 'small' : 'medium'}
                                label={
                                    discountType === 'percentage'
                                        ? 'Descuento (%)'
                                        : 'Descuento (monto)'
                                }
                                type="number"
                                value={tier.discount_value}
                                onChange={(e) =>
                                    onTierChange(tier.localId, {
                                        discount_value: e.target.value,
                                    })
                                }
                                inputProps={{
                                    min: 0,
                                    step: discountType === 'percentage' ? 1 : 0.01,
                                    max: discountType === 'percentage' ? 100 : undefined,
                                }}
                                InputProps={{ startAdornment: valueAdornment }}
                                error={Boolean(discountError)}
                                helperText={discountError || undefined}
                            />
                        </Grid>
                    </Grid>
                </Paper>
                );
            })}
        </Box>
    );
};

export default PricingTiersSection;
